import { Injectable, inject } from '@angular/core';

export interface DialogButton {
  text: string;
  value: boolean;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface DialogConfig {
  title: string;
  message: string;
  icon?: 'warning' | 'error' | 'info' | 'question';
  buttons?: DialogButton[];
  timer?: number;
  showTimer?: boolean;
  allowOutsideClick?: boolean;
  allowEscapeKey?: boolean;
}

export interface DialogResult {
  confirmed: boolean;
  value?: any;
  dismissed?: boolean;
  timedOut?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private activeDialog: any = null;

  /**
   * Show confirmation dialog
   */
  async confirm(config: DialogConfig): Promise<DialogResult> {
    // Prevent multiple dialogs
    if (this.activeDialog) {
      await this.dismissActiveDialog();
    }

    try {
      // Try to use SweetAlert2 if available
      const swalModule = await import('sweetalert2').catch(() => null);
      
      if (swalModule) {
        const Swal = swalModule.default;
        
        const result = await Swal.fire({
          title: config.title,
          text: config.message,
          icon: config.icon || 'question',
          showCancelButton: config.buttons ? config.buttons.length > 1 : true,
          confirmButtonText: config.buttons?.[0]?.text || 'Yes',
          cancelButtonText: config.buttons?.[1]?.text || 'No',
          allowOutsideClick: config.allowOutsideClick ?? false,
          allowEscapeKey: config.allowEscapeKey ?? false,
          timer: config.timer,
          timerProgressBar: config.showTimer ?? false,
          customClass: {
            confirmButton: this.getButtonClass(config.buttons?.[0]?.style),
            cancelButton: this.getButtonClass(config.buttons?.[1]?.style)
          }
        });

        return {
          confirmed: result.isConfirmed,
          value: result.value,
          dismissed: result.isDismissed,
          timedOut: result.dismiss === Swal.DismissReason.timer
        };
      } else {
        // Fallback to browser confirm if SweetAlert2 not available
        return this.fallbackConfirm(config);
      }

    } catch (importError) {
      console.warn('SweetAlert2 not available, using browser confirm');
      return this.fallbackConfirm(config);
    }
  }

  /**
   * Show alert dialog
   */
  async alert(title: string, message: string, icon: 'warning' | 'error' | 'info' = 'info'): Promise<void> {
    try {
      const swalModule = await import('sweetalert2').catch(() => null);
      
      if (swalModule) {
        const Swal = swalModule.default;
        
        await Swal.fire({
          title,
          text: message,
          icon,
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false
        });
      } else {
        window.alert(`${title}\n\n${message}`);
      }
    } catch (importError) {
      window.alert(`${title}\n\n${message}`);
    }
  }

  /**
   * Show session warning dialog
   */
  async showSessionWarning(timeRemaining: number): Promise<boolean> {
    const minutes = Math.floor(timeRemaining / 60000);
    
    const result = await this.confirm({
      title: 'Session Warning',
      message: `Your session will expire in ${minutes} minute${minutes !== 1 ? 's' : ''} due to inactivity. Do you want to continue?`,
      icon: 'warning',
      buttons: [
        { text: 'Continue Session', value: true, style: 'primary' },
        { text: 'Logout Now', value: false, style: 'secondary' }
      ],
      timer: timeRemaining,
      showTimer: true,
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    return result.confirmed;
  }

  /**
   * Show token expired dialog
   */
  async showTokenExpired(): Promise<void> {
    await this.alert(
      'Session Expired',
      'Your session has expired. You will be redirected to the login page.',
      'error'
    );
  }

  /**
   * Show session expired dialog
   */
  async showSessionExpired(): Promise<void> {
    await this.alert(
      'Session Expired',
      'You have been logged out due to inactivity.',
      'info'
    );
  }

  /**
   * Show logout confirmation
   */
  async confirmLogout(): Promise<boolean> {
    const result = await this.confirm({
      title: 'Confirm Logout',
      message: 'Are you sure you want to logout?',
      icon: 'question',
      buttons: [
        { text: 'Yes, Logout', value: true, style: 'danger' },
        { text: 'Cancel', value: false, style: 'secondary' }
      ]
    });

    return result.confirmed;
  }

  /**
   * Show token refresh warning
   */
  async showTokenRefreshWarning(): Promise<boolean> {
    const result = await this.confirm({
      title: 'Session About to Expire',
      message: 'Your session is about to expire. Do you want to extend it?',
      icon: 'warning',
      buttons: [
        { text: 'Extend Session', value: true, style: 'primary' },
        { text: 'Logout', value: false, style: 'secondary' }
      ],
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    return result.confirmed;
  }

  /**
   * Show company switch confirmation
   */
  async confirmCompanySwitch(fromCompany: string, toCompany: string): Promise<boolean> {
    const result = await this.confirm({
      title: 'Switch Company',
      message: `Switch from ${fromCompany} to ${toCompany}?`,
      icon: 'question',
      buttons: [
        { text: 'Switch', value: true, style: 'primary' },
        { text: 'Cancel', value: false, style: 'secondary' }
      ]
    });

    return result.confirmed;
  }

  /**
   * Show error dialog
   */
  async showError(title: string, message: string): Promise<void> {
    await this.alert(title, message, 'error');
  }

  /**
   * Show success message (toast-like)
   */
  async showSuccess(title: string, message: string): Promise<void> {
    try {
      const { default: Swal } = await import('sweetalert2');
      
      await Swal.fire({
        title,
        text: message,
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    } catch (importError) {
      // Fallback for success - could integrate with toast service
      console.log(`${title}: ${message}`);
    }
  }

  /**
   * Dismiss active dialog
   */
  async dismissActiveDialog(): Promise<void> {
    if (this.activeDialog) {
      try {
        const { default: Swal } = await import('sweetalert2');
        Swal.close();
      } catch (importError) {
        // Can't dismiss browser dialogs programmatically
      }
      this.activeDialog = null;
    }
  }

  /**
   * Check if dialog is currently active
   */
  isDialogActive(): boolean {
    return this.activeDialog !== null;
  }

  /**
   * Private helper methods
   */

  /**
   * Get CSS class for button style
   */
  private getButtonClass(style?: string): string {
    switch (style) {
      case 'primary':
        return 'btn btn-primary';
      case 'danger':
        return 'btn btn-danger';
      case 'secondary':
      default:
        return 'btn btn-secondary';
    }
  }

  /**
   * Fallback confirm using browser dialog
   */
  private fallbackConfirm(config: DialogConfig): Promise<DialogResult> {
    return new Promise((resolve) => {
      const message = `${config.title}\n\n${config.message}`;
      const confirmed = window.confirm(message);
      
      resolve({
        confirmed,
        value: confirmed,
        dismissed: !confirmed
      });
    });
  }

  /**
   * Show custom dialog with multiple options
   */
  async showCustomDialog(config: DialogConfig & { 
    options: Array<{ text: string; value: any; style?: string }> 
  }): Promise<any> {
    try {
      const { default: Swal } = await import('sweetalert2');
      
      // Create custom HTML for multiple buttons
      const buttonsHtml = config.options.map((option, index) => 
        `<button type="button" class="swal2-styled ${this.getButtonClass(option.style)}" data-value="${index}">${option.text}</button>`
      ).join(' ');

      const result = await Swal.fire({
        title: config.title,
        text: config.message,
        icon: config.icon,
        showConfirmButton: false,
        showCancelButton: false,
        html: `
          <p>${config.message}</p>
          <div class="swal2-actions">
            ${buttonsHtml}
          </div>
        `,
        didOpen: () => {
          const buttons = Swal.getPopup()?.querySelectorAll('[data-value]');
          buttons?.forEach(button => {
            button.addEventListener('click', (e) => {
              const target = e.target as HTMLElement;
              const value = parseInt(target.getAttribute('data-value') || '0');
              Swal.close();
              (Swal as any)._resolveValue = config.options[value].value;
            });
          });
        }
      });

      return (Swal as any)._resolveValue || null;

    } catch (importError) {
      // Fallback to first option confirm
      return this.fallbackConfirm(config);
    }
  }
}
